export const ALL_COLORS = "__ALL_COLORS__";

const colors: Record<string, { value: string; label: string }[]> = {
  gold: [
    { value: "amarillo", label: "Amarillo" },
    { value: "blanco", label: "Blanco" },
    { value: "rojo", label: "Rojo" },
  ],
  silver: [
    { value: "plata", label: "Plata" },
    { value: "soaked", label: "Empavonado" },
    { value: "rose", label: "Rosé" },
    { value: "golden", label: "Dorado" },
  ],
};

export const getVariantColors = (material: string) => colors[material] || [];

// Decimal commas and the existing "mediana" gauge suffix are numeric for
// presentation only. Invalid/legacy measurements remain visible at the end.
const numericValue = (value?: string) => {
  const text = String(value ?? "").trim();
  if (!/^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)(?:\s+mediana)?$/i.test(text)) {
    return Infinity;
  }
  const number = Number.parseFloat(text.replace(",", "."));
  return Number.isFinite(number) ? number : Infinity;
};

export const compareVariantNumbers = (left?: string, right?: string) => {
  const a = numericValue(left);
  const b = numericValue(right);
  return a === b ? 0 : a < b ? -1 : 1;
};

type SortableVariant = Pick<Variation, "length" | "color" | "gauge">;

export const sortVariantRows = <T extends SortableVariant>(rows: readonly T[], material: string): T[] => {
  const options = getVariantColors(material);
  const rank = (color: string) => {
    const index = options.findIndex((option) => option.value === color);
    return index < 0 ? options.length : index;
  };
  return [...rows].sort((a, b) =>
    compareVariantNumbers(a.length, b.length) ||
    rank(a.color) - rank(b.color) ||
    compareVariantNumbers(a.gauge, b.gauge)
  );
};

export const groupVariantRows = <T extends SortableVariant>(rows: readonly T[], material: string) => {
  const groups = new Map<string, T[]>();
  rows.forEach((row) => {
    const gauge = row.gauge ?? "";
    groups.set(gauge, [...(groups.get(gauge) || []), row]);
  });
  return Array.from(groups, ([gauge, variations]) => ({
    gauge, variations: sortVariantRows(variations, material),
  })).sort((a, b) => compareVariantNumbers(a.gauge, b.gauge));
};

// Preserve the admin form's established uniqueness rule, scoped to product.
// Weight, workmanship, and wastage do not distinguish duplicate additions.
type VariantIdentity = Pick<Variation, "productId" | "gauge" | "length" | "color">;
export const isDuplicateVariant = (a: VariantIdentity, b: VariantIdentity) =>
  a.productId === b.productId && a.gauge === b.gauge &&
  a.length === b.length && a.color === b.color;
